import React, { useState, useEffect } from 'react';
import { Text } from '@components/Text';
import { RedesignButton } from '@components/RedesignButton';
import { Dropdown } from '@components/Dropdown';
import { InputTextarea } from 'primereact/inputtextarea';
import { closeCurrentModalAction } from 'src/redesign/features/Modals/store/reducer';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import { toastNotificationsSlice } from 'src/redesign/features/Toast/store/reducer';
import { getEventTicketsWithoutMetrics } from 'src/redesign/features/Events/store/actions';
import { getEventTicketsSelector } from 'src/redesign/features/Events/store/selectors';
import moment from 'moment-timezone';
import S from './RequestModal.module.scss';

interface TransferRequestModalProps {
  request: any;
  onApprove?: (
    requestId: string,
    comment: string,
    ticketSelections?: Record<string, string>,
  ) => void;
  onDeny?: (requestId: string, comment: string) => void;
}

export const TransferRequestModal: React.FC<TransferRequestModalProps> = ({
  request,
  onApprove,
  onDeny,
}) => {
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [ticketSelections, setTicketSelections] = useState<Record<string, string>>({});

  const dispatch = useAppDispatch();
  const availableTickets = useAppSelector(getEventTicketsSelector);

  // Fetch tickets for the target event when modal opens
  useEffect(() => {
    if (request.target_event?.id) {
      dispatch(getEventTicketsWithoutMetrics(request.target_event.id));
    }
  }, [dispatch, request.target_event?.id]);

  // Auto-select first ticket for each attendee when tickets are loaded
  useEffect(() => {
    if (availableTickets?.length > 0 && request.attendees?.length > 0) {
      const firstTicket = availableTickets[0];
      const initialSelections: Record<string, string> = {};

      request.attendees.forEach((attendee: any) => {
        if (!ticketSelections[attendee.id]) {
          initialSelections[attendee.id] = firstTicket.id;
        }
      });

      if (Object.keys(initialSelections).length > 0) {
        setTicketSelections((prev) => ({ ...prev, ...initialSelections }));
      }
    }
  }, [availableTickets, request.attendees]);

  const handleClose = () => {
    dispatch(closeCurrentModalAction());
  };

  const handleApprove = async () => {
    setIsSubmitting(true);
    try {
      if (onApprove) {
        await onApprove(request.id, comment, ticketSelections);
      }

      dispatch(
        toastNotificationsSlice.actions.showNotification({
          severity: 'success',
          detail: 'Transfer request approved successfully.',
          life: 5000,
        }),
      );

      handleClose();
    } catch (error) {
      dispatch(
        toastNotificationsSlice.actions.showNotification({
          severity: 'error',
          detail: 'Failed to approve request. Please try again.',
          life: 5000,
        }),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeny = async () => {
    setIsSubmitting(true);
    try {
      if (onDeny) {
        await onDeny(request.id, comment);
      }

      dispatch(
        toastNotificationsSlice.actions.showNotification({
          severity: 'success',
          detail: 'Transfer request declined.',
          life: 5000,
        }),
      );

      handleClose();
    } catch (error) {
      dispatch(
        toastNotificationsSlice.actions.showNotification({
          severity: 'error',
          detail: 'Failed to decline request. Please try again.',
          life: 5000,
        }),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Format event date/time with timezone
  const formatEventDateTime = (dateString: string, timezone?: string) => {
    const eventMoment = timezone ? moment.tz(dateString, timezone) : moment(dateString);
    return eventMoment.format('dddd, MMMM Do YYYY [at] h:mm A z');
  };

  return (
    <div className={S.modalContent}>
      <div className={S.reasonSection}>
        <Text variation="body-small" bold className={S.sectionLabel}>
          Reason:
        </Text>
        <Text variation="body" className={S.reason}>
          {request.reason}
        </Text>
      </div>

      <div className={S.detailsSection}>
        <div className={S.transferSummary}>
          <Text variation="body-small" bold className={S.sectionLabel}>
            Transfer Request:
          </Text>
          <div className={S.transferLine}>
            <Text variation="body" className={S.eventInfo}>
              {request.event.title} -{' '}
              {formatEventDateTime(request.event.start_date, request.event.timezone)}
            </Text>
            <Text variation="body" color="brand-400" className={S.arrow}>
              →
            </Text>
            <Text variation="body" className={S.eventInfo}>
              {request.target_event?.title} -{' '}
              {request.target_event?.start_date
                ? formatEventDateTime(
                    request.target_event.start_date,
                    request.target_event.timezone,
                  )
                : 'Date TBD'}
            </Text>
          </div>
        </div>

        <div className={S.detailRow}>
          <Text variation="body-small" bold className={S.detailLabel}>
            Attendees & Ticket Assignment:
          </Text>
          <div className={S.attendeesWithTickets}>
            {request.attendees.map((attendee: any, index: number) => (
              <div key={attendee.id} className={S.attendeeTicketRow}>
                <div className={S.attendeeInfo}>
                  <Text variation="body">
                    {attendee.first_name} {attendee.last_name} ({attendee.email})
                  </Text>
                </div>
                <div className={S.ticketSelection}>
                  {availableTickets?.length > 0 ? (
                    <Dropdown
                      value={ticketSelections[attendee.id] || ''}
                      onChange={(e) =>
                        setTicketSelections((prev) => ({
                          ...prev,
                          [attendee.id]: e.value,
                        }))
                      }
                      options={availableTickets.map((ticket: any) => ({
                        label: ticket.name,
                        value: ticket.id,
                      }))}
                      placeholder="Select ticket..."
                      className={S.ticketDropdown}
                    />
                  ) : (
                    <Text variation="body-small" color="black-400">
                      Loading tickets...
                    </Text>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className={S.detailRow}>
          <Text variation="body-small" bold className={S.detailLabel}>
            Order ID:
          </Text>
          <Text variation="body">#{request.cart.friendly_cart_id}</Text>
        </div>
      </div>

      <div className={S.commentSection}>
        <Text variation="body-small" bold className={S.sectionLabel}>
          Admin Comment (Optional):
        </Text>
        <InputTextarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Add a comment for the attendee..."
          className={S.commentTextarea}
          rows={3}
        />
      </div>

      <div className={S.modalActions}>
        <RedesignButton
          variation="secondary"
          height={52}
          onClick={handleDeny}
          disabled={isSubmitting}
          className={S.denyButton}
        >
          <Text regular color="error-400">
            {isSubmitting ? 'Processing...' : 'Decline'}
          </Text>
        </RedesignButton>

        <RedesignButton
          variation="primary"
          height={52}
          onClick={handleApprove}
          disabled={isSubmitting}
          className={S.approveButton}
        >
          <Text regular color="white-100">
            {isSubmitting ? 'Processing...' : 'Accept'}
          </Text>
        </RedesignButton>
      </div>
    </div>
  );
};
